import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

const prose =
  "text-pretty text-base leading-relaxed text-slate-600 dark:text-slate-400";

const labelClass =
  "text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400";

const subBlockTitle =
  "mb-2 text-sm font-semibold text-slate-800 dark:text-slate-200";

const articlePad = "scroll-mt-28 p-6 sm:p-8";

function Impressum() {
  const { t } = useTranslation();
  const base = process.env.PUBLIC_URL || "";

  const statutesHref = useMemo(
    () => `${base}/documents/Satzung-GCWAA-2.pdf`.replace(/([^:]\/)\/+/g, "$1"),
    [base]
  );

  return (
    <section
      className="border-t border-slate-200 bg-slate-50/90 py-12 transition-colors dark:border-slate-700 dark:bg-slate-900/50 md:py-14"
      id="impressum"
      aria-labelledby="impressum-heading"
    >
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <h2
          id="impressum-heading"
          className="scroll-mt-28 text-pretty text-3xl font-semibold tracking-tight text-primary dark:text-primary-light md:text-4xl"
        >
          {t("impressum.title")}
        </h2>
        <p
          className="mt-2 text-sm text-slate-500 dark:text-slate-400"
          aria-hidden
        >
          <span className="inline-block h-0.5 w-12 rounded-full bg-primary dark:bg-primary-light" />
        </p>

        <div className="mt-10 divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:divide-slate-600 dark:border-slate-600 dark:bg-slate-800">
          <article id="impressum-organization" className={articlePad}>
            <h3 className="mb-6 text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">
              {t("impressum.organization.name")}
            </h3>
            <dl className="space-y-1">
              <dt className={labelClass}>
                {t("impressum.organization.address.title")}
              </dt>
              <dd className={prose}>
                <p>{t("impressum.organization.address.line1")}</p>
                <p>{t("impressum.organization.address.line2")}</p>
              </dd>
            </dl>
          </article>

          <article id="impressum-registration" className={articlePad}>
            <h3 className="mb-6 text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">
              {t("impressum.registration.title")}
            </h3>
            <dl className="grid gap-6 sm:grid-cols-2 sm:gap-8">
              <div>
                <dt className={labelClass}>
                  {t("impressum.registration.number.title")}
                </dt>
                <dd className={`mt-2 ${prose}`}>
                  {t("impressum.registration.number.value")}
                </dd>
              </div>
              <div>
                <dt className={labelClass}>
                  {t("impressum.registration.tax.title")}
                </dt>
                <dd className={`mt-2 ${prose}`}>
                  {t("impressum.registration.tax.value")}
                </dd>
              </div>
            </dl>
          </article>

          <article id="impressum-statutes" className={articlePad}>
            <h3 className="mb-4 text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">
              {t("impressum.statutes.title")}
            </h3>
            <a
              href={statutesHref}
              download="GCWAA-Satzung.pdf"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 rounded-2xl border border-slate-200 border-l-4 border-l-primary bg-slate-50/80 p-4 shadow-sm transition-all dark:border-slate-600 dark:border-l-primary-light dark:bg-slate-900/40 motion-safe:hover:shadow-md sm:gap-5 sm:p-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 dark:focus-visible:ring-primary-light/40 dark:focus-visible:ring-offset-slate-800"
            >
              <div
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 transition-transform duration-300 dark:bg-primary-light/15 sm:h-14 sm:w-14 motion-safe:group-hover:scale-105"
                aria-hidden
              >
                <i className="fas fa-file-pdf text-xl text-primary dark:text-primary-light sm:text-2xl" />
              </div>
              <div className="min-w-0 flex-1 text-left">
                <span className="block text-base font-semibold text-slate-900 dark:text-slate-100 sm:text-lg">
                  {t("impressum.statutes.linkText")}
                </span>
                <span className="mt-0.5 block text-sm text-slate-500 dark:text-slate-400">
                  {t("impressum.statutes.file_hint")}
                </span>
              </div>
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-200/80 text-slate-600 transition-colors group-hover:bg-primary/10 group-hover:text-primary dark:bg-slate-700 dark:text-slate-300 dark:group-hover:bg-primary-light/15 dark:group-hover:text-primary-light"
                aria-hidden
              >
                <i className="fas fa-download text-sm" />
              </span>
            </a>
          </article>

          <article id="impressum-board" className={articlePad}>
            <h3 className="mb-4 text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">
              {t("impressum.board.title")}
            </h3>
            <ul className={`space-y-2 ${prose}`}>
              <li>{t("impressum.board.president")}</li>
              <li>{t("impressum.board.vicePresident")}</li>
            </ul>
          </article>

          <article id="impressum-disclaimer" className={articlePad}>
            <h3 className="mb-6 text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">
              {t("impressum.disclaimer.title")}
            </h3>
            <div className="space-y-6">
              <div>
                <h4 className={subBlockTitle}>
                  {t("impressum.disclaimer.content.title")}
                </h4>
                <p className={prose}>
                  {t("impressum.disclaimer.content.text")}
                </p>
              </div>
              <div>
                <h4 className={subBlockTitle}>
                  {t("impressum.disclaimer.links.title")}
                </h4>
                <p className={prose}>{t("impressum.disclaimer.links.text")}</p>
              </div>
            </div>
          </article>

          <article id="impressum-copyright" className={articlePad}>
            <h3 className="mb-4 text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">
              {t("impressum.copyright.title")}
            </h3>
            <p className={prose}>{t("impressum.copyright.text")}</p>
          </article>

          <article id="impressum-privacy-note" className={articlePad}>
            <h3 className="mb-4 text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">
              {t("impressum.privacy.title")}
            </h3>
            <p className={prose}>{t("impressum.privacy.text")}</p>
          </article>

          <article id="impressum-legal-validity" className={articlePad}>
            <h3 className="mb-4 text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">
              {t("impressum.legalValidity.title")}
            </h3>
            <p className={prose}>{t("impressum.legalValidity.text")}</p>
          </article>
        </div>
      </div>
    </section>
  );
}

export default Impressum;
